import React, { useEffect, useMemo, useRef } from 'react';
import { Play } from 'lucide-react';
import type { SessionToolCall } from '@/lib/api/sessions';
import SyncedDomPlayer from '@/components/sessions/SyncedDomPlayer';

interface ContentViewerProps {
  toolCalls: SessionToolCall[];
  /** Current audio playback time in seconds (always provided, even when paused). */
  audioTime: number;
  /** Whether the session audio is currently playing. */
  isAudioPlaying: boolean;
  sessionStartTime?: string;
  backgroundScreenshotUrl?: string | null;
}

type MediaItem = {
  toolCall: SessionToolCall;
  offsetSeconds: number;
  type: 'slide' | 'video' | 'dom';
  imageUrl?: string;
  videoUrl?: string;
  domRecordingUrl?: string;
  startTime?: number;
  endTime?: number;
  domStartOffset?: number;
  title?: string;
};

function computeVideoTargetTime(
  clipStart: number,
  clipDuration: number | null,
  audioTime: number,
  mediaOffset: number
): number {
  const relative = Math.max(0, audioTime - mediaOffset);
  const clampedRelative = clipDuration != null ? Math.min(relative, clipDuration) : relative;
  return clipStart + clampedRelative;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function pushSegment(
  items: MediaItem[],
  tc: SessionToolCall,
  segment: Record<string, any>,
  offsetSeconds: number,
  fallbackTitle?: string,
) {
  if (segment.dom_recording_url && !segment.video_url) {
    const domStartOffset = toFiniteNumber(segment.dom_start_offset ?? segment.start_time);
    items.push({
      toolCall: tc,
      offsetSeconds,
      type: 'dom',
      domRecordingUrl: segment.dom_recording_url,
      domStartOffset,
      title: segment.title || fallbackTitle,
    });
  } else if (segment.video_url) {
    const startTime = toFiniteNumber(segment.start_time);
    const endTime = toFiniteNumber(segment.end_time);
    items.push({
      toolCall: tc,
      offsetSeconds,
      type: 'video',
      videoUrl: segment.video_url,
      startTime,
      endTime,
      title: segment.title || fallbackTitle,
    });
  }
}

function extractMediaItems(
  toolCalls: SessionToolCall[],
  sessionStartTime: string | undefined
): MediaItem[] {
  if (!sessionStartTime) return [];
  const startMs = new Date(sessionStartTime).getTime();
  if (Number.isNaN(startMs)) return [];

  const items: MediaItem[] = [];

  for (const tc of toolCalls) {
    const outputs = tc.outputs_json;
    if (!outputs) continue;

    const offsetMs = tc.created_at
      ? new Date(tc.created_at).getTime() - startMs
      : 0;
    const offsetSeconds = Math.max(0, offsetMs / 1000);

    const messageType = outputs.type;

    if (messageType === 'presentation_slide' && outputs.image_url) {
      items.push({
        toolCall: tc,
        offsetSeconds,
        type: 'slide',
        imageUrl: outputs.image_url,
        title: outputs.title,
      });
    } else if (
      (messageType === 'video_segment' || messageType === 'dom_segment') &&
      (outputs.video_url || outputs.dom_recording_url)
    ) {
      pushSegment(items, tc, outputs, offsetSeconds);
    } else if (messageType === 'segment_playlist' && outputs.segments?.length) {
      // Expand playlist segments sequentially in timeline order.
      let cumulativeOffset = 0;
      for (const segment of outputs.segments) {
        const segmentOffset = offsetSeconds + cumulativeOffset;
        pushSegment(items, tc, segment, segmentOffset, outputs.title);

        const start = toFiniteNumber(
          segment.type === 'dom_segment' ? (segment.segment_start_time ?? segment.start_time) : segment.start_time
        ) ?? 0;
        const end = toFiniteNumber(segment.end_time) ?? start;
        cumulativeOffset += Math.max(0, end - start);
      }
    }
  }

  items.sort((a, b) => a.offsetSeconds - b.offsetSeconds);
  return items;
}

const ContentViewer: React.FC<ContentViewerProps> = ({
  toolCalls,
  audioTime,
  isAudioPlaying,
  sessionStartTime,
  backgroundScreenshotUrl,
}) => {
  const mediaItems = useMemo(
    () => extractMediaItems(toolCalls, sessionStartTime),
    [toolCalls, sessionStartTime]
  );

  // Find the active media item: the latest one whose offset ≤ current audio time
  const activeMedia = useMemo(() => {
    if (mediaItems.length === 0) return null;
    let best: MediaItem | null = null;
    for (const item of mediaItems) {
      if (item.offsetSeconds <= audioTime) {
        best = item;
      }
    }
    return best;
  }, [audioTime, mediaItems]);

  return (
    <div className="w-full bg-black overflow-hidden">
      {activeMedia ? (
        <ContentMediaRenderer
          media={activeMedia}
          audioTime={audioTime}
          isPlaying={isAudioPlaying}
        />
      ) : backgroundScreenshotUrl ? (
        <img
          src={backgroundScreenshotUrl}
          alt="Session background"
          className="w-full max-h-[70vh] object-contain"
        />
      ) : (
        <div className="flex items-center justify-center aspect-video">
          <div className="flex flex-col items-center gap-2 text-white/40">
            <Play className="h-10 w-10" />
            <span className="text-xs">Demo will appear here during playback</span>
          </div>
        </div>
      )}
    </div>
  );
};

function ContentMediaRenderer({
  media,
  audioTime,
  isPlaying,
}: {
  media: MediaItem;
  audioTime: number;
  isPlaying: boolean;
}) {
  if (media.type === 'slide' && media.imageUrl) {
    return (
      <img
        src={media.imageUrl}
        alt={media.title || 'Slide'}
        className="w-full max-h-[70vh] object-contain bg-black"
      />
    );
  }

  if (media.type === 'video' && media.videoUrl) {
    return (
      <SyncedVideoPlayer
        media={media}
        audioTime={audioTime}
        isPlaying={isPlaying}
      />
    );
  }

  if (media.type === 'dom' && media.domRecordingUrl) {
    return (
      <div className="w-full aspect-video bg-black">
        <SyncedDomPlayer
          sourceUrl={media.domRecordingUrl}
          audioCurrentTime={audioTime}
          isPlaying={isPlaying}
          segmentStartOffset={media.offsetSeconds}
          recordingStartOffsetSeconds={media.domStartOffset ?? 0}
          className="w-full h-full"
        />
      </div>
    );
  }

  return null;
}

function SyncedVideoPlayer({
  media,
  audioTime,
  isPlaying,
}: {
  media: MediaItem;
  audioTime: number;
  isPlaying: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const clipStart = media.startTime ?? 0;
  const clipEnd = media.endTime;
  const clipDuration = clipEnd != null ? Math.max(0, clipEnd - clipStart) : null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const relative = audioTime - media.offsetSeconds;
    const inClipRange = relative >= 0 && (clipDuration == null || relative <= clipDuration);
    const targetTime = computeVideoTargetTime(clipStart, clipDuration, audioTime, media.offsetSeconds);
    const drift = Math.abs(video.currentTime - targetTime);

    if (!isPlaying || !inClipRange) {
      if (drift > 0.05) {
        try {
          video.currentTime = targetTime;
        } catch (e) {
          console.warn('Video seek failed:', e);
        }
      }
      video.pause();
      return;
    }

    if (drift > 0.35) {
      try {
        video.currentTime = targetTime;
      } catch (e) {
        console.warn('Video seek failed:', e);
      }
    }

    if (video.paused) {
      video.play().catch((e) => console.warn('Video play failed:', e));
    }
  }, [audioTime, clipDuration, clipStart, isPlaying, media.offsetSeconds]);

  return (
    <video
      key={`${media.toolCall.id}-${media.offsetSeconds}-${media.videoUrl}`}
      ref={videoRef}
      src={media.videoUrl}
      muted
      playsInline
      className="w-full max-h-[70vh] object-contain bg-black"
      onLoadedMetadata={() => {
        const video = videoRef.current;
        if (!video) return;
        const targetTime = computeVideoTargetTime(clipStart, clipDuration, audioTime, media.offsetSeconds);
        try {
          video.currentTime = targetTime;
        } catch (e) {
          console.warn('Video seek failed:', e);
        }
      }}
    />
  );
}

export default ContentViewer;
